import { PROVIDERS, type AgentAccount, type AgentProvider, type GithubStatus } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

import { accountsApi } from "@/api/accounts.ts";
import { githubApi, googleApi } from "@/api/imports.ts";
import { settingsApi } from "@/api/settings.ts";
import { Github } from "lucide-react";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Card, Field, Input, Select } from "@/ui/primitives.tsx";
import { LoginPanel } from "./LoginPanel.tsx";
import { useApiError } from "@/ui/useApiError.ts";

export function AccountsSettings({ isAdmin, canAdd = true }: { isAdmin: boolean; canAdd?: boolean }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list });
  const [form, setForm] = useState({ label: "", shared: false, provider: "claude" as AgentProvider });
  const [loginFor, setLoginFor] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["accounts"] });
  const create = useMutation({ mutationFn: () => accountsApi.create({ provider: form.provider, label: form.label, shared: form.shared }), onSuccess: (a) => { setForm({ label: "", shared: false, provider: "claude" }); void refresh(); setLoginFor(a.id); } });
  const check = useMutation({ mutationFn: accountsApi.refresh, onSuccess: refresh });
  const { confirm } = useConfirm();
  const logout = useMutation({ mutationFn: accountsApi.logout, onSuccess: refresh });
  const remove = useMutation({ mutationFn: accountsApi.remove, onSuccess: refresh });
  const share = useMutation({ mutationFn: (a: AgentAccount) => accountsApi.setShared(a.id, !a.shared), onSuccess: refresh });
  const askShare = async (a: AgentAccount) => {
    if (!a.shared || await confirm({ title: t("confirm.unshareAccountTitle"), message: t("confirm.unshareAccountMsg", { label: a.label }), confirmLabel: t("accounts.unshare"), danger: true })) share.mutate(a);
  };
  const askLogout = async (a: AgentAccount) => { if (await confirm({ title: t("confirm.logoutAccountTitle"), message: t("confirm.logoutAccountMsg", { label: a.label }), confirmLabel: t("accounts.logout"), danger: true })) logout.mutate(a.id); };
  const askRemove = async (a: AgentAccount) => { if (await confirm({ title: t("confirm.deleteAccountTitle"), message: t("confirm.deleteAccountMsg", { label: a.label }), confirmLabel: t("common.delete"), danger: true })) remove.mutate(a.id); };
  const onExit = useCallback(() => { void refresh(); }, []);
  const gh = useQuery({ queryKey: ["github", "status"], queryFn: githubApi.status });
  const ghDisconnect = useMutation({ mutationFn: githubApi.disconnect, onSuccess: () => void qc.invalidateQueries({ queryKey: ["github"] }) });
  const gg = useQuery({ queryKey: ["google", "status"], queryFn: googleApi.status });
  const policy = useQuery({ queryKey: ["settings", "agents"], queryFn: settingsApi.agents, enabled: isAdmin });
  const savePolicy = useMutation({ mutationFn: settingsApi.setAgents, onSuccess: (d) => qc.setQueryData(["settings", "agents"], d) });
  const ggDisconnect = useMutation({ mutationFn: googleApi.disconnect, onSuccess: () => void qc.invalidateQueries({ queryKey: ["google"] }) });

  return (
    <div className="space-y-4">
      <Alert tone="info">{t("accounts.intro")}</Alert>
      {isAdmin && policy.data && (
        <Card className="space-y-3">
          <h3 className="font-semibold">{t("accounts.policy.title")}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("accounts.policy.maxRuns")} hint={t("accounts.policy.maxRunsHint")}>
              <Input dir="ltr" type="number" min={1} max={50} value={policy.data.maxConcurrentRuns} onChange={(e) => savePolicy.mutate({ ...policy.data!, maxConcurrentRuns: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })} />
            </Field>
            <Field label={t("accounts.policy.replyLanguage")} hint={t("accounts.policy.replyLanguageHint")}>
              <Select value={policy.data.replyLanguage} onChange={(e) => savePolicy.mutate({ ...policy.data!, replyLanguage: e.target.value as "auto" | "ar" | "en" })}>
                <option value="auto">{t("accounts.policy.auto")}</option>
                <option value="ar">العربية</option>
                <option value="en">English</option>
              </Select>
            </Field>
          </div>
          {savePolicy.error && <Alert tone="danger">{toMsg(savePolicy.error)}</Alert>}
        </Card>
      )}
      <Card className="flex flex-wrap items-center gap-3">
        <span className="grid size-5 place-items-center rounded-full bg-white text-[11px] font-bold text-[#4285F4]">G</span>
        <div className="min-w-48 flex-1">
          <div className="font-medium">Google Drive</div>
          <div className="text-xs text-fg-muted">{gg.data?.connected ? t("import.ghConnectedAs", { login: gg.data.email }) : gg.data && !gg.data.configured ? t("drive.notConfigured") : t("drive.why")}</div>
        </div>
        {gg.data?.connected ? (
          <Button variant="secondary" size="sm" loading={ggDisconnect.isPending} onClick={() => ggDisconnect.mutate()}>{t("import.ghDisconnect")}</Button>
        ) : (
          <a className={cn("rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg", !gg.data?.configured && "pointer-events-none opacity-50")} href={googleApi.startUrl}>{t("drive.connect")}</a>
        )}
        {gg.data && !gg.data.configured && (
          <div className="basis-full rounded-md border border-border bg-surface-2/40 px-3 py-2 text-xs text-fg-muted">
            <div className="mb-1 font-medium text-fg">{t("drive.setupTitle")}</div>
            <ol className="list-decimal space-y-0.5 ps-5">
              <li>{t("drive.setup1")}</li>
              <li>{t("drive.setup2")} <code dir="ltr" className="select-all break-all rounded bg-surface px-1">{`${window.location.origin}/api/integrations/google/callback`}</code> {t("drive.setup2b")} <code dir="ltr" className="select-all break-all rounded bg-surface px-1">{`${window.location.origin}/api/auth/google/callback`}</code></li>
              <li>{t("drive.setup3")} <code dir="ltr" className="select-all break-all rounded bg-surface px-1">GOOGLE_CLIENT_ID</code> / <code dir="ltr" className="select-all break-all rounded bg-surface px-1">GOOGLE_CLIENT_SECRET</code></li>
            </ol>
          </div>
        )}
      </Card>
      <Card className="flex flex-wrap items-center gap-3">
        <Github className="size-5" />
        <div className="min-w-48 flex-1">
          <div className="font-medium">GitHub</div>
          <div className="text-xs text-fg-muted">{gh.data?.connected ? t("import.ghConnectedAs", { login: gh.data.login }) : gh.data && !gh.data.configured ? t("import.ghNotConfigured") : t("import.ghWhy")}</div>
        </div>
        {gh.data?.connected ? (
          <Button variant="secondary" size="sm" loading={ghDisconnect.isPending} onClick={() => ghDisconnect.mutate()}>{t("import.ghDisconnect")}</Button>
        ) : (
          <a className={cn("rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg", !gh.data?.configured && "pointer-events-none opacity-50")} href={githubApi.startUrl}>{t("import.ghConnect")}</a>
        )}
        {gh.data && isAdmin && <GithubAppSetup status={gh.data} onSaved={(s) => qc.setQueryData(["github", "status"], s)} />}
      </Card>
      {/* adding an account of one's own takes the "accounts" feature */}
      {canAdd && <Card>
        <form className="grid items-end gap-3 sm:grid-cols-[auto_1fr_auto_auto]" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <Field label={t("accounts.provider")}>
            <Select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value as AgentProvider })}>
              {(Object.keys(PROVIDERS) as AgentProvider[]).map((id) => <option key={id} value={id}>{PROVIDERS[id].label}{PROVIDERS[id].beta ? " (beta)" : ""}</option>)}
            </Select>
          </Field>
          <Field label={t("accounts.label")}>
            <Input required maxLength={60} placeholder={t("accounts.labelPlaceholder")} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>
          {isAdmin ? (
            <label className="flex h-10 items-center gap-2 text-sm">
              <input type="checkbox" checked={form.shared} onChange={(e) => setForm({ ...form, shared: e.target.checked })} /> {t("accounts.shared")}
            </label>
          ) : <span />}
          <Button type="submit" loading={create.isPending}>{t("accounts.add")}</Button>
        </form>
        {create.error && <Alert tone="danger" className="mt-3">{toMsg(create.error)}</Alert>}
      </Card>}
      {(check.error || logout.error || remove.error || share.error) && <Alert tone="danger">{toMsg(check.error ?? logout.error ?? remove.error ?? share.error)}</Alert>}
      {q.data?.length === 0 && <Card className="text-sm text-fg-muted">{t("accounts.empty")}</Card>}
      <ul className="space-y-3">
        {(q.data ?? []).map((a) => (
          <li key={a.id}>
            <Card className="space-y-3">
              <AccountRow a={a} busy={check.isPending && check.variables === a.id} onLogin={() => setLoginFor(loginFor === a.id ? null : a.id)} onCheck={() => check.mutate(a.id)} onLogout={() => void askLogout(a)} onRemove={() => void askRemove(a)} onShare={isAdmin ? () => void askShare(a) : undefined} sharing={share.isPending && share.variables?.id === a.id} loginOpen={loginFor === a.id} />
              {loginFor === a.id && (
                <LoginPanel accountId={a.id} onDone={onExit} />
              )}
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AccountRow({ a, busy, loginOpen, onLogin, onCheck, onLogout, onRemove, onShare, sharing }: { a: AgentAccount; busy: boolean; loginOpen: boolean; onLogin: () => void; onCheck: () => void; onLogout: () => void; onRemove: () => void; onShare?: (() => void) | undefined; sharing: boolean }) {
  const { t, i18n } = useTranslation();
  const tone = { pending: "bg-surface-2 text-fg-muted", connected: "bg-success-soft text-success", disconnected: "bg-warn-soft text-fg", error: "bg-danger-soft text-danger" }[a.status];
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{a.label}</span>
          <span className="inline-flex items-center gap-1 rounded bg-surface-2 px-1.5 py-0.5 text-xs text-fg-muted"><ProviderIcon provider={a.provider} size="xs" />{PROVIDERS[a.provider].label}{PROVIDERS[a.provider].beta ? " · beta" : ""}</span>
          <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", tone)}>{t(`accounts.status.${a.status}`)}</span>
          {a.shared && <span className="rounded bg-accent-soft px-1.5 py-0.5 text-xs">{t("accounts.sharedBadge")}</span>}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-fg-muted">
          {a.info?.email && <span dir="ltr">{a.info.email}</span>}
          {a.info?.subscriptionType && <span>{a.info.subscriptionType}</span>}
          <span>{t("accounts.owner")}: {a.ownerName}</span>
          <span>{t("accounts.projectsUsing", { count: a.projectsUsing })}</span>
          {a.checkedAt && <span>{t("accounts.checked")}: {fmt(a.checkedAt)}</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={onCheck} loading={busy}><RefreshCw className="size-3.5" /> {t("accounts.check")}</Button>
        {a.status !== "connected" && <Button size="sm" onClick={onLogin}>{loginOpen ? t("common.close") : t("accounts.login")}</Button>}
        {a.status === "connected" && <Button size="sm" variant="secondary" onClick={onLogout}>{t("accounts.logout")}</Button>}
        {onShare && <Button size="sm" variant="secondary" onClick={onShare} loading={sharing}>{a.shared ? t("accounts.unshare") : t("accounts.share")}</Button>}
        {a.projectsUsing === 0 && <Button size="sm" variant="ghost" onClick={onRemove}>{t("common.delete")}</Button>}
      </div>
    </div>
  );
}

/** Admin: the GitHub OAuth app behind "Connect GitHub", entered here (encrypted) or via GITHUB_CLIENT_ID/SECRET in the environment. */
function GithubAppSetup({ status, onSaved }: { status: GithubStatus; onSaved: (s: GithubStatus) => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const [clientId, setClientId] = useState(status.source === "settings" ? (status.clientId ?? "") : "");
  const [secret, setSecret] = useState("");
  const [open, setOpen] = useState(!status.configured);
  const save = useMutation({ mutationFn: () => githubApi.setApp(clientId.trim(), secret.trim()), onSuccess: (s) => { setSecret(""); setOpen(false); onSaved(s); } });
  const clear = useMutation({ mutationFn: githubApi.clearApp, onSuccess: (s) => { setClientId(""); onSaved(s); } });
  const origin = window.location.origin;
  return (
    <div className="basis-full rounded-md border border-border bg-surface-2/40 px-3 py-2 text-xs text-fg-muted">
      <button type="button" className="flex w-full items-center justify-between text-start font-medium text-fg" onClick={() => setOpen(!open)}>
        <span>{t("ghApp.title")}{status.configured && <span className="ms-2 text-success">✓ {status.source === "env" ? t("ghApp.fromEnv") : t("ghApp.fromSettings")}</span>}</span>
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <ol className="list-decimal space-y-0.5 ps-5">
            <li>{t("ghApp.step1")} <a className="underline" href="https://github.com/settings/developers" target="_blank" rel="noreferrer">github.com/settings/developers</a></li>
            <li>{t("ghApp.step2")} <code dir="ltr" className="select-all break-all rounded bg-surface px-1">{origin}</code></li>
            <li>{t("ghApp.step3")} <code dir="ltr" className="select-all break-all rounded bg-surface px-1">{`${origin}/api/integrations/github/callback`}</code>{status.callbackUrl && !status.callbackUrl.startsWith(origin) && <span className="ms-1">{t("ghApp.alsoAdd")} <code dir="ltr" className="select-all break-all rounded bg-surface px-1">{status.callbackUrl}</code></span>}</li>
            <li>{t("ghApp.step4")}</li>
          </ol>
          {status.source === "env" ? <p>{t("ghApp.envNote")}</p> : (
            <form className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]" onSubmit={(e) => { e.preventDefault(); if (clientId.trim() && secret.trim()) save.mutate(); }}>
              <Input dir="ltr" className="h-8 font-mono text-xs" placeholder="Client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} />
              <Input dir="ltr" type="password" className="h-8 font-mono text-xs" placeholder="Client Secret" value={secret} onChange={(e) => setSecret(e.target.value)} />
              <div className="flex gap-1">
                <Button type="submit" size="sm" className="h-8" disabled={!clientId.trim() || !secret.trim()} loading={save.isPending}>{t("common.save")}</Button>
                {status.source === "settings" && <Button type="button" size="sm" variant="ghost" className="h-8" loading={clear.isPending} onClick={() => clear.mutate()}>{t("ghApp.remove")}</Button>}
              </div>
            </form>
          )}
          {(save.error || clear.error) && <Alert tone="danger">{toMsg(save.error ?? clear.error)}</Alert>}
        </div>
      )}
    </div>
  );
}
